import { useCallback, useEffect, useRef, useState } from "react";
import { InvoiceModel } from "../models/InvoiceModel";
import salesService from "../services/salesService";
import { formatPrice } from "../utils/currencyUtils";
import styles from './BillingPage.module.css';
import FileDownloadComp, { FileDownloadCompRef } from "../components/FileDownloadComp";
import { format } from "date-fns";
import PaginationComp from "../components/PaginationComp";

const InvoiceStateMap: Record<string, { label: string; badgeColor: string }> = {
    created: { label: 'Unpaid', badgeColor: 'bg-danger' },
    sent: { label: 'Unpaid', badgeColor: 'bg-danger' },
    paid: { label: 'Paid', badgeColor: 'bg-success' },
};

const getStatusLabel = (invoice: InvoiceModel): string => {
    const status = InvoiceStateMap[invoice.status.toLowerCase()]?.label ?? "Unknown";
    if (status === 'Unpaid' && new Date() > invoice.dueDate) {
        return 'Overdue';
    }
    return status;
}

const BillingPage = () => {

    const pageSize = 10;

    const currentDate = new Date();
    const lastYearDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 11, 1);
    const currentMonth = currentDate.getFullYear() + "-" + (currentDate.getMonth() + 1).toString().padStart(2, '0');
    const lastYearMonth = lastYearDate.getFullYear() + "-" + (lastYearDate.getMonth() + 1).toString().padStart(2, '0');

    const [beginMonth, setBeginMonth] = useState<string>(lastYearMonth);
    const [endMonth, setEndMonth] = useState<string>(currentMonth);
    const [beginMonthInput, setBeginMonthInput] = useState<string>(lastYearMonth);
    const [endMonthInput, setEndMonthInput] = useState<string>(currentMonth);

    const [invoices, setInvoices] = useState<InvoiceModel[]>([]);
    const [totalInvoices, setTotalInvoices] = useState<number>(0);
    const [pageNum, setPageNum] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [error, setError] = useState<{ type: "validation" | "api" | null, message: string }>({ type: null, message: '' });

    const fileDownloader = useRef<FileDownloadCompRef>(null);

    const fetchInvoices = useCallback(async () => {
        setIsLoading(true);
        setError({ type: null, message: '' });
        try {
            const offset = pageNum * pageSize;
            const response = await salesService.getInvoices(beginMonth, endMonth, offset, pageSize);
            const fetchedInvoices = response.invoices.map((invData: any) => new InvoiceModel(invData));
            setInvoices(fetchedInvoices);
            setTotalInvoices(response.invoiceCount);
        } catch (error: any) {
            setError({ type: 'api', message: 'Failed to fetch invoices.' });
        } finally {
            setIsLoading(false);
        }
    }, [beginMonth, endMonth, pageNum]);

    useEffect(() => {
        fetchInvoices();
    }, [fetchInvoices]);

    const billingPeriodValidation = (beginMonthValue: string, endMonthValue: string): boolean => {
        const begin = new Date(beginMonthValue + '-01');
        const end = new Date(endMonthValue + '-01');
        return begin <= end;
    }

    const changeBeginMonth = (newBeginMonth: string) => {
        setBeginMonthInput(newBeginMonth);
        if (billingPeriodValidation(newBeginMonth, endMonthInput)) {
            setBeginMonth(newBeginMonth);
            setEndMonth(endMonthInput);
        } else {
            setError({ type: 'validation', message: 'Begin month cannot be later than end month.' });
        }
    }

    const changeEndMonth = (newEndMonth: string) => {
        setEndMonthInput(newEndMonth);
        if (billingPeriodValidation(beginMonthInput, newEndMonth)) {
            setBeginMonth(beginMonthInput);
            setEndMonth(newEndMonth);
        } else {
            setError({ type: 'validation', message: 'Begin month cannot be later than end month.' });
        }
    }

    const changePage = (newPageNum: number) => {
        setPageNum(newPageNum);
    };

    const downloadInvoice = (invoiceId: number) => {
        if (fileDownloader.current) {
            fileDownloader.current.downloadFile(`Sales/Invoice/${invoiceId}/PDF`);
        }
    }

    return (
        <div className='mt-3'>
            <div className='row mb-5'>
                <div className='col-12'>
                    <div className="card rounded-0">
                        <div className="card-body p-4">
                            <h5>Billing</h5>
                            <p className="mb-0 mt-3">Billing Period</p>
                            <div className='row mb-1'>
                                <div className="col-md-2">
                                    <div className="input-group mt-2">
                                        <input
                                            type="month"
                                            className={`form-control`}
                                            value={beginMonthInput}
                                            onChange={(e) => changeBeginMonth(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className={`col-md-1 ${styles.billingPeriodTo}`}>to</div>
                                <div className="col-md-2">
                                    <div className="input-group mt-2">
                                        <input
                                            type="month"
                                            className={`form-control`}
                                            value={endMonthInput}
                                            onChange={(e) => changeEndMonth(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                            {error.type === "validation" && (<p className="text-danger mb-0 mt-1">{error.message}</p>)}
                        </div>
                    </div>
                </div>
                {error.type === "api" && <div className="col-md-12 mt-3 clearfix"><div className="alert alert-danger p-2 mb-0">{error.message}</div></div>}
                <div className="col-md-12 mt-3">
                    {isLoading ? (
                        <div className="text-center gap-2 mt-3 text-secondary">
                            <div className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></div>
                            <span>  Loading...</span>
                        </div>
                    ) : (
                        /* Empty results */
                        invoices.length === 0 ? (
                            <div className="border fs-4 mt-4 p-5 text-center">
                                <i className="bi bi-stars clearfix"></i>
                                <span className='clearfix'>There are no invoices for this billing period.</span>
                            </div>
                        ) : (
                            /* Invoice list */
                            invoices.length > 0 && (
                                <div className='col-12'>
                                    <div className="card rounded-0">
                                        <div className="card-body p-4">
                                            <h5 className="mb-3">Invoices</h5>
                                            <FileDownloadComp ref={fileDownloader} />
                                            <table className="table table-bordered">
                                                <thead>
                                                    <tr>
                                                        <th scope="col">Invoice No.</th>
                                                        <th scope="col">Issue Date</th>
                                                        <th scope="col">Due Date</th>
                                                        <th scope="col">Amount</th>
                                                        <th scope="col">Status</th>
                                                        <th scope="col" style={{ width: 100 }}></th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {invoices.map(invoice => (
                                                        <tr key={invoice.invoiceId}>
                                                            <td className="align-middle">{invoice.invoiceId}</td>
                                                            <td className="align-middle">{format(invoice.issueDate, "dd/MM/yyyy")}</td>
                                                            <td className="align-middle">{format(invoice.dueDate, "dd/MM/yyyy")}</td>
                                                            <td className="align-middle">{formatPrice(invoice.invoiceAmount)}</td>
                                                            <td className="align-middle"><span className={`badge ${InvoiceStateMap[invoice.status.toLowerCase()]?.badgeColor ?? 'bg-secondary'} rounded-0`}>{getStatusLabel(invoice)}</span></td>
                                                            <td className="align-middle"><button className="btn btn-outline-dark btn-sm rounded-0" onClick={() => downloadInvoice(invoice.invoiceId)}>Download</button></td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )
                        )
                    )}
                </div>
                {/* Pagination */}
                {!isLoading && invoices.length > 0 && (
                    <PaginationComp pageNum={pageNum} pageSize={pageSize} totalCount={totalInvoices} onPageChange={changePage}></PaginationComp>
                )}
            </div>
        </div>
    );
}

export default BillingPage;